import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router';

import type { BaseCampaign, CampaignState } from '@feathr/blackbox';
import { CampaignClass } from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import { Button, DebouncedInput, Input, Table, Toolbar, UniversalSelect } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useFlags, useLocalUrl, useStore } from '@feathr/extender/state';

import eventPartnersMessagesColumns from './eventPartnersMessagesColumns';

import * as styles from './EventPartnersMessagesPage.css';

interface IFilters {
  name__icontains?: string;
  state?: CampaignState;
}

function EventPartnersMessagesPage(): JSX.Element {
  const localUrl = useLocalUrl();
  const { eventId } = useParams<{ eventId: string }>();
  const [filters, setFilters] = useState<IFilters>({
    name__icontains: undefined,
  });
  const history = useHistory();
  const { mailrail } = useFlags();

  const { Campaigns } = useStore();

  async function createMessage(): Promise<void> {
    const message = Campaigns.create({
      _cls: mailrail ? CampaignClass.MailrailPartnerMessage : CampaignClass.PinpointPartnerMessage,
      event: eventId,
      participation: {
        partner_ids: [],
        mode: 'event',
      },
    });
    const response = await Campaigns.add(message, { validate: false });
    history.push(localUrl(response.getItemUrl()));
  }

  function handleDebouncedSearchChange(newValue?: string): void {
    setFilters({
      ...filters,
      name__icontains: newValue,
    });
  }

  function handleClearState(): void {
    setFilters({ ...filters, state: undefined });
  }

  function handleSelectState(option: IUniversalSelectOption): void {
    setFilters({ ...filters, state: option.id as CampaignState });
  }

  const actions = (
    <Toolbar>
      <Button
        onClick={createMessage}
        prefix={<FontAwesomeIcon icon={faPaperPlane} />}
        type={'primary'}
      >
        New message
      </Button>
    </Toolbar>
  );

  const stateOptions: IUniversalSelectOption[] = [
    { id: 'draft', name: 'Draft' },
    { id: 'stopped', name: 'Stopped' },
    { id: 'erroring', name: 'Erroring' },
    { id: 'published', name: 'Published' },
    { id: 'publishing', name: 'Publishing' },
  ];
  const stateValue = stateOptions.find((option) => option.id === filters.state);
  const filterElements = [
    <UniversalSelect<IUniversalSelectOption>
      isClearable={true}
      key={'partner-message-status'}
      name={'partner-message-status'}
      onClear={handleClearState}
      onSelectSingle={handleSelectState}
      options={stateOptions}
      placeholder={'Status'}
      value={stateValue}
      wrapperClassName={styles.filter}
    />,
    <DebouncedInput<string>
      defaultValue={filters.name__icontains}
      key={'partner-message-search'}
      onChange={handleDebouncedSearchChange}
    >
      {(liveValue, onChangeLiveValue) => (
        <Input
          onChange={onChangeLiveValue}
          placeholder={'Search by name...'}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          type={'text'}
          value={liveValue}
        />
      )}
    </DebouncedInput>,
  ];

  return (
    <Page actions={actions} title={'Messages'}>
      <Table<BaseCampaign>
        collection={Campaigns}
        columns={eventPartnersMessagesColumns}
        filterElements={filterElements}
        filters={{
          ...filters,
          _cls__in: [CampaignClass.PinpointPartnerMessage, CampaignClass.MailrailPartnerMessage],
          event: eventId,
          is_archived__ne: true,
        }}
        initialSort={[{ id: 'date_created' }]}
      />
    </Page>
  );
}

export default observer(EventPartnersMessagesPage);
